import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { supabase } from '../../../lib/supabase';

const LOGS_PER_PAGE = 15;

const AuditLog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterUser, setFilterUser] = useState('all');
  const [auditLogs, setAuditLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageInput, setPageInput] = useState('');

  const fetchAuditLogs = async (page = 1) => {
    try {
      setIsLoading(true);
      const from = (page - 1) * LOGS_PER_PAGE;
      const to = from + LOGS_PER_PAGE - 1;

      const { data, error, count } = await supabase
        .from('audit_logs')
        .select('id, action_type, target_email, target_user_id, description, performed_by_label, metadata, created_at', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(from, to);

      if (error) throw error;
      setAuditLogs(data || []);
      setTotalCount(count || 0);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs(currentPage);
  }, [currentPage]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const typeOptions = [
    { value: 'all', label: 'All Actions' },
    { value: 'user_management', label: 'User Management' },
    { value: 'subscription', label: 'Subscriptions' },
    { value: 'configuration', label: 'Configuration' },
    { value: 'payment', label: 'Payments' }
  ];

  const getTypeFromActionType = (actionType) => {
    if (!actionType) return 'other';
    if (actionType.startsWith('user_')) return 'user_management';
    if (actionType.startsWith('subscription_') || actionType.startsWith('plan_')) return 'subscription';
    if (actionType.startsWith('payment_') || actionType.startsWith('revenue_')) return 'payment';
    if (actionType.startsWith('config_') || actionType.startsWith('system_')) return 'configuration';
    return 'user_management';
  };

  const getActionIcon = (type) => {
    const icons = {
      user_management: 'Users',
      subscription: 'Crown',
      configuration: 'Settings',
      payment: 'CreditCard'
    };
    return icons?.[type] || 'Activity';
  };

  const getActionColor = (type) => {
    const colors = {
      user_management: 'text-primary',
      subscription: 'text-accent',
      configuration: 'text-secondary',
      payment: 'text-success'
    };
    return colors?.[type] || 'text-muted-foreground';
  };

  const formatTimestamp = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Build user options from current page logs that match category filter
  const userOptions = (() => {
    const logsForCategory = filterType === 'all'
      ? auditLogs
      : auditLogs.filter((log) => getTypeFromActionType(log.action_type) === filterType);

    const emailSet = new Set();
    logsForCategory.forEach((log) => {
      if (log.target_email) emailSet.add(log.target_email);
    });

    const options = [{ value: 'all', label: 'All Users' }];
    [...emailSet].sort().forEach((email) => {
      options.push({ value: email, label: email });
    });
    return options;
  })();

  // Reset user filter when category changes
  useEffect(() => {
    setFilterUser('all');
  }, [filterType]);

  const filteredLogs = auditLogs.filter((log) => {
    const type = getTypeFromActionType(log.action_type);
    const actionLabel = log.action_type?.replace(/_/g, ' ') || '';
    const matchesSearch =
      actionLabel.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.target_email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.description || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (log.performed_by_label || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === 'all' || type === filterType;
    const matchesUser = filterUser === 'all' || log.target_email === filterUser;
    return matchesSearch && matchesType && matchesUser;
  });

  const totalPages = Math.ceil(totalCount / LOGS_PER_PAGE);

  return (
    <div className="bg-card rounded-lg border border-border shadow-soft">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="FileText" size={24} className="text-primary" />
            Audit Log
          </h2>
          <div className="flex items-center gap-3">
            <div className="flex-1 lg:w-64">
              <Input
                type="search"
                placeholder="Search audit logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e?.target?.value)}
              />
            </div>
            <Select
              options={typeOptions}
              value={filterType}
              onChange={setFilterType}
              className="w-48"
            />
            <Select
              options={userOptions}
              value={filterUser}
              onChange={setFilterUser}
              className="w-56"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <>
          <div className="divide-y divide-border">
            {filteredLogs.map((log) => {
              const type = getTypeFromActionType(log.action_type);
              const actionLabel = log.action_type
                ? log.action_type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                : '—';
              return (
                <div key={log.id} className="p-6 hover:bg-muted/30 transition-smooth">
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${getActionColor(type)}`}>
                      <Icon name={getActionIcon(type)} size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4 mb-2">
                        <div>
                          <h3 className="font-semibold text-foreground">{actionLabel}</h3>
                          {log.target_email && (
                            <p className="text-sm text-muted-foreground mt-1">
                              Target: <span className="font-medium text-foreground">{log.target_email}</span>
                            </p>
                          )}
                        </div>
                        <span className="text-sm text-muted-foreground whitespace-nowrap">
                          {formatTimestamp(log.created_at)}
                        </span>
                      </div>
                      {log.description && (
                        <p className="text-sm text-muted-foreground mb-2">{log.description}</p>
                      )}
                      {log.metadata && (log.metadata.amount != null || log.metadata.plan_name) && (
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          {log.metadata.amount != null && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-xs font-medium text-foreground">
                              <Icon name="DollarSign" size={12} />
                              ${log.metadata.amount} {log.metadata.currency?.toUpperCase() || 'USD'}
                            </span>
                          )}
                          {log.metadata.plan_name && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-xs font-medium text-foreground">
                              <Icon name="Crown" size={12} />
                              {log.metadata.plan_name}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="User" size={14} />
                        <span>By: {log.performed_by_label || 'System'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {filteredLogs.length === 0 && (
            <div className="p-12 text-center">
              <Icon name="FileText" size={48} className="mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground">No audit logs found matching your criteria</p>
            </div>
          )}

          {/* Pagination */}
          {totalCount > LOGS_PER_PAGE && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-border">
              <span className="text-sm text-muted-foreground">
                {(currentPage - 1) * LOGS_PER_PAGE + 1}–{Math.min(currentPage * LOGS_PER_PAGE, totalCount)} of {totalCount}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Prev
                </button>
                {(() => {
                  const startPage = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  const endPage = Math.min(totalPages, startPage + 4);
                  const pages = [];
                  for (let i = startPage; i <= endPage; i++) pages.push(i);
                  return pages.map((p) => (
                    <button
                      key={p}
                      onClick={() => handlePageChange(p)}
                      className={`min-w-[36px] py-1.5 rounded-lg text-sm font-medium transition-smooth ${currentPage === p ? 'bg-primary text-primary-foreground' : 'border border-border hover:bg-muted text-foreground'}`}
                    >
                      {p}
                    </button>
                  ));
                })()}
                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-smooth border border-border hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  Next
                </button>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const num = parseInt(pageInput, 10);
                    if (num >= 1 && num <= totalPages) handlePageChange(num);
                    setPageInput('');
                  }}
                  className="flex items-center gap-1.5 ml-3"
                >
                  <input
                    type="text"
                    value={pageInput}
                    onChange={(e) => setPageInput(e.target.value.replace(/\D/g, ''))}
                    placeholder="Go to"
                    className="w-16 px-2 py-1.5 rounded-lg text-sm border border-border bg-background text-foreground text-center focus:outline-none focus:border-primary"
                  />
                  <button
                    type="submit"
                    className="px-2.5 py-1.5 rounded-lg text-sm font-medium border border-border hover:bg-muted transition-smooth"
                  >
                    Go
                  </button>
                </form>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AuditLog;
